import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Tv, Plus, ArrowRight, ShieldCheck, Clock, CheckCircle2, XCircle, Ban,
} from "lucide-react";
import { toast } from "sonner";

// Read-mostly cubicle list — surfaces to:
//   • Channel/OTT admins → desks their org has requested/attached to
//   • Desk members → the specific shows they oversee
//   • PH owners → desks (any status) attached to projects they own
export default function ShowDesksList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [desks, setDesks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/show-desks/mine");
      setDesks(data.desks || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const isChannel = user?.account_type === "studio_network_admin"
    || user?.account_type === "admin";

  const tabs = {
    active:    desks.filter((d) => d.status === "active"),
    requested: desks.filter((d) => d.status === "requested"),
    rejected:  desks.filter((d) => d.status === "rejected"),
    revoked:   desks.filter((d) => d.status === "revoked"),
  };
  const [tab, setTab] = useState("active");

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10"
      data-testid="show-desks-list-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Channel / OTT
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
            Show Desks
          </h1>
          <p className="mt-2 text-sm text-white/55 max-w-2xl">
            One read-mostly cubicle per show. Attach to a Production House&apos;s
            TV project, invite your programming team, and oversee the show
            without touching your other slate.
          </p>
        </div>
        {isChannel && (
          <Button
            onClick={() => nav("/channel/show-desks/attach")}
            className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="show-desk-attach-btn"
          >
            <Plus size={14} className="mr-2" /> Attach to a Show
          </Button>
        )}
      </div>

      {/* Status tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 mb-6"
        data-testid="show-desks-tabs">
        {[
          { k: "active",    label: "Active",    Icon: CheckCircle2 },
          { k: "requested", label: "Pending",   Icon: Clock },
          { k: "rejected",  label: "Rejected",  Icon: XCircle },
          { k: "revoked",   label: "Revoked",   Icon: Ban },
        ].map(({ k, label, Icon }) => (
          <button key={k}
            onClick={() => setTab(k)}
            className={"px-4 py-2.5 text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors " +
              (tab === k ? "border-[color:var(--association-theme)] text-white"
                         : "border-transparent text-white/45 hover:text-white/70")}
            data-testid={`show-desks-tab-${k}`}>
            <Icon size={12} />
            {label}
            <span className="ml-1 text-white/40">({tabs[k].length})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-white/55 py-16 text-center">Loading…</div>
      ) : tabs[tab].length === 0 ? (
        <div className="cs-card rounded-2xl p-12 text-center" data-testid="show-desks-empty">
          <Tv size={36} className="mx-auto text-white/30 mb-4" />
          <div className="font-display text-2xl uppercase tracking-tight">
            No {tab} desks
          </div>
          <div className="mt-2 text-sm text-white/55">
            {tab === "active"
              ? isChannel
                ? "Attach a Show Desk to a Production House's TV project to get started."
                : "You have no active Show Desks yet. Ask your Programming Head for an invite."
              : `No ${tab} desks to show.`}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {tabs[tab].map((d) => (
            <Link key={d.id}
              to={`/channel/show-desks/${d.id}`}
              className="cs-card rounded-2xl p-6 hover:translate-y-[-2px] transition-transform"
              data-testid={`show-desk-card-${d.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">
                    {d.project_type?.replace(/_/g, " ")}
                  </div>
                  <div className="font-display text-2xl uppercase tracking-tight mt-1 truncate">
                    {d.project_title}
                  </div>
                  <div className="mt-2 text-xs text-white/55">
                    Channel: {d.channel_org_name || "—"} · PH: {d.ph_org_name || "—"}
                  </div>
                </div>
                <StatusChip status={d.status} />
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4">
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                  Requested {d.requested_at?.slice(0, 10)}
                </span>
                <ArrowRight size={14} className="text-white/55" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active:    { bg: "bg-emerald-500/15", fg: "text-emerald-300", Icon: CheckCircle2, label: "Active" },
    requested: { bg: "bg-amber-500/15",   fg: "text-amber-300",   Icon: Clock,         label: "Pending" },
    rejected:  { bg: "bg-red-500/15",     fg: "text-red-300",     Icon: XCircle,       label: "Rejected" },
    revoked:   { bg: "bg-white/10",       fg: "text-white/55",    Icon: Ban,           label: "Revoked" },
  };
  const s = map[status] || map.revoked;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${s.bg} ${s.fg}`}
      data-testid={`show-desk-status-${status}`}>
      <s.Icon size={10} /> {s.label}
    </span>
  );
}
